import { FormEvent, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { CreateAffiliateBody, useAdminAffiliateList, useCreateAffiliate } from 'src/hooks/useAdminAffiliates'
import { AffiliateStatus } from 'src/types/Affiliate'
import { formatBps, formatCents, formatDay } from 'src/utils/affiliateFormat'
import { notify } from 'src/utils/notify'
import { AFFILIATE_STATUS_OPTIONS, AffiliateStatusBadge, errorMessage } from '../affiliate/labels'
import {
    Card,
    CardTitle,
    EmptyRow,
    ErrorText,
    Grid,
    InlineSpinner,
    Input,
    Label,
    Muted,
    Pager,
    PrimaryButton,
    Row,
    Segmented,
    Select,
    Table,
    TableWrap,
    TextArea,
} from '../affiliate/ui'
import {
    CODE_PATTERN,
    ClickRow,
    FormError,
    Negative,
    formMessage,
    readDollars,
    readPercent,
    useClampPage,
    useDebounced,
    userLabel,
} from './shared'

const AffiliatesTable = () => {
    const history = useHistory()
    const [status, setStatus] = useState<AffiliateStatus | ''>('')
    const [search, setSearch] = useState('')
    const q = useDebounced(search.trim())
    const [page, setPage] = useState(1)

    useEffect(() => setPage(1), [status, q])

    const { data, isLoading, error, isFetching, isPlaceholderData } = useAdminAffiliateList({ status, q, page })
    useClampPage(page, data?.totalPages, setPage, isPlaceholderData)

    return (
        <Card>
            <Row>
                <CardTitle>Affiliates</CardTitle>
                {isFetching && !isLoading && <Muted>Refreshing…</Muted>}
            </Row>
            <Grid $min={220}>
                <Label>
                    Status
                    <Select value={status} onChange={(e) => setStatus(e.target.value as AffiliateStatus | '')}>
                        <option value="">All statuses</option>
                        {AFFILIATE_STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                </Label>
                <Label>
                    Search
                    <Input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Code, email or Discord username"
                    />
                </Label>
            </Grid>
            {isLoading ? (
                <InlineSpinner />
            ) : error ? (
                <ErrorText>{errorMessage(error)}</ErrorText>
            ) : (
                <>
                    <TableWrap>
                        <Table>
                            <thead>
                                <tr>
                                    <th>Code</th>
                                    <th>User</th>
                                    <th>Status</th>
                                    <th className="num">First / recurring</th>
                                    <th className="num">Active refs</th>
                                    <th className="num">Lifetime</th>
                                    <th className="num">Available</th>
                                    <th>Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data && data.results.length > 0 ? (
                                    data.results.map((row) => (
                                        <ClickRow key={row.id} onClick={() => history.push(`/admin/affiliates/${row.id}`)}>
                                            <td>
                                                <strong>{row.code}</strong>
                                            </td>
                                            <td>{userLabel(row.user)}</td>
                                            <td>
                                                <AffiliateStatusBadge status={row.status} />
                                            </td>
                                            <td className="num">
                                                {formatBps(row.rates.firstMonthBps)} / {formatBps(row.rates.recurringBps)}
                                            </td>
                                            <td className="num">{row.referralsActive}</td>
                                            <td className="num">{formatCents(row.lifetimeCents)}</td>
                                            <td className="num">
                                                <Negative $negative={row.availableCents < 0}>
                                                    {formatCents(row.availableCents)}
                                                </Negative>
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>{formatDay(row.createdAt)}</td>
                                        </ClickRow>
                                    ))
                                ) : (
                                    <EmptyRow colSpan={8}>No affiliates found.</EmptyRow>
                                )}
                            </tbody>
                        </Table>
                    </TableWrap>
                    {data && <Pager page={data.page} totalPages={data.totalPages} onChange={setPage} />}
                </>
            )}
        </Card>
    )
}

type IdKind = 'userId' | 'discordId'

const EnableAffiliateForm = () => {
    const history = useHistory()
    const create = useCreateAffiliate()
    const [idKind, setIdKind] = useState<IdKind>('discordId')
    const [identifier, setIdentifier] = useState('')
    const [code, setCode] = useState('')
    const [firstMonth, setFirstMonth] = useState('')
    const [recurring, setRecurring] = useState('')
    const [threshold, setThreshold] = useState('')
    const [notes, setNotes] = useState('')

    const submit = (e: FormEvent) => {
        e.preventDefault()
        let body: CreateAffiliateBody
        try {
            const id = identifier.trim()
            if (idKind === 'userId' && !/^[a-f0-9]{24}$/i.test(id)) {
                throw new FormError('User ID must be a 24-character Mongo ObjectId.')
            }
            if (idKind === 'discordId' && !/^\d+$/.test(id)) throw new FormError('Discord ID must be digits only.')
            const normalizedCode = code.trim().toLowerCase()
            if (normalizedCode && !CODE_PATTERN.test(normalizedCode)) {
                throw new FormError('Codes are 3–20 characters: lowercase letters, numbers and dashes.')
            }
            body = {
                ...(idKind === 'userId' ? { userId: id } : { discordId: id }),
                code: normalizedCode || undefined,
                firstMonthBps: readPercent(firstMonth, 'First-month rate'),
                recurringBps: readPercent(recurring, 'Recurring rate'),
                minPayoutCents: readDollars(threshold, 'Payout threshold'),
                notes: notes.trim() || undefined,
            }
        } catch (err) {
            notify(formMessage(err) || 'Check the form.', 'error')
            return
        }
        create.mutate(body, {
            onSuccess: (affiliate) => {
                notify(`Affiliate ${affiliate.code} enabled.`, 'success')
                history.push(`/admin/affiliates/${affiliate.id}`)
            },
            onError: (err) => notify(errorMessage(err), 'error'),
        })
    }

    return (
        <Card as="form" onSubmit={submit}>
            <CardTitle>Enable affiliate</CardTitle>
            <Muted>Makes an existing user an active affiliate. They accept the terms on their first visit.</Muted>
            <Segmented
                options={[
                    { value: 'discordId', label: 'Discord ID' },
                    { value: 'userId', label: 'User ID' },
                ]}
                value={idKind}
                onChange={setIdKind}
            />
            <Grid $min={200}>
                <Label>
                    {idKind === 'userId' ? 'User ID' : 'Discord ID'}
                    <Input
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        placeholder={idKind === 'userId' ? '64f0c2…' : '123456789012345678'}
                        required
                    />
                </Label>
                <Label>
                    Code (optional)
                    <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="auto" />
                </Label>
                <Label>
                    First month % (optional)
                    <Input inputMode="decimal" value={firstMonth} onChange={(e) => setFirstMonth(e.target.value)} placeholder="default" />
                </Label>
                <Label>
                    Recurring % (optional)
                    <Input inputMode="decimal" value={recurring} onChange={(e) => setRecurring(e.target.value)} placeholder="default" />
                </Label>
                <Label>
                    Payout threshold $ (optional)
                    <Input inputMode="decimal" value={threshold} onChange={(e) => setThreshold(e.target.value)} placeholder="default" />
                </Label>
            </Grid>
            <Label>
                Notes (optional)
                <TextArea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={5000} />
            </Label>
            <Row>
                <PrimaryButton type="submit" disabled={create.isPending || !identifier.trim()}>
                    {create.isPending ? 'Enabling…' : 'Enable affiliate'}
                </PrimaryButton>
            </Row>
        </Card>
    )
}

const AffiliatesTab = () => (
    <>
        <AffiliatesTable />
        <EnableAffiliateForm />
    </>
)

export default AffiliatesTab
